import * as React from 'react'
import { Suggestion } from '@tiptap/suggestion'
import { Extension } from '@tiptap/core'
import { ReactRenderer } from '@tiptap/react'
import tippy, { type Instance, type Props } from 'tippy.js'
import { tabContentId } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/tab-content/wrapper'
import { PluginKey } from '@tiptap/pm/state'
import { create } from 'zustand'
import { ComponentInstanceIcon } from '@radix-ui/react-icons'

import { queryMatches } from '~/lib/simple-input-search'

import type {
  ComponentBlock,
  RichTextBlock,
} from '@basehub/data/replicache/index'
import {
  type CommandCategory,
  type Command,
  type SlashCommandType,
  type CommandListComponentProps,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/slash-command'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { CodeBlock } from '@basehub/tiptap-api-utils'

export const SlashCommands = Extension.create<SlashCommandType>({
  name: 'slash-command',

  addOptions() {
    return {
      component: null,
      richTextId: '',
      suggestion: {
        char: '/',
        pluginKey: 'suggestion-plugin',
      },
      onOpen: () => undefined,
      onClose: () => undefined,
      onSelectBlock: () => Promise.resolve(),
      onCreateComponentBlock: () => Promise.resolve({ id: '' }),
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
        pluginKey: new PluginKey(this.options.suggestion.pluginKey),
        allow({ editor }) {
          if (!editor.isFocused) return false
          if (editor.isActive(CodeBlock.name)) return false
          return true
        },

        items: ({ editor: _, query }) => {
          const { getAcceptedComponents, commands } =
            useSlashCommandStore.getState()

          const filteredComponents = {
            id: 'instances',
            name: 'Components',
            commands: getAcceptedComponents(this.options.richTextId).reduce<
              Command[]
            >((acc, c) => {
              const { displayTitle, displayDescription } = getBlockDisplayData({
                block: c,
                parentBlock: null,
              })
              const component: Command = {
                id: idToSourceBlockId(c),
                title: displayTitle,
                description:
                  displayDescription ||
                  `Create an instance of "${displayTitle}"`,
                keywords: [c.title, idToSourceBlockId(c)],
                icon: <ComponentInstanceIcon className="w-7" />,
                ...(c.typeOptions.displayImage
                  ? { preview: c.typeOptions.displayImage }
                  : {}),
                command: async ({ editor, range }) => {
                  const nodeAfter = editor.view.state.selection.$to.nodeAfter
                  const overrideSpace = nodeAfter?.text?.startsWith(' ')

                  if (overrideSpace) range.to += 1
                  const newBlock = await this.options.onSelectBlock({
                    ...c,
                    newBlockType: c.type,
                  })

                  if (!newBlock) return

                  editor
                    .chain()
                    .setBaseHubBlock({ id: idToSourceBlockId(newBlock) })
                    .focus()
                    .run()
                },
              }

              if (queryMatches(query, component.title)) acc.push(component)

              return acc
            }, []),
          }

          const filteredCommands = commands.reduce<CommandCategory[]>(
            (acc, current) => {
              const result = filterCommandCategory(query, current)
              if (result) {
                acc.push(result)
              }
              return acc
            },
            []
          )

          if (!filteredCommands.length && !filteredComponents.commands.length) {
            return []
          }

          return [
            ...(filteredComponents.commands.length ? [filteredComponents] : []),
            ...filteredCommands,
          ]
        },
        command: ({ editor, range, props }) => {
          const command = props as unknown as Command
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          command.command({
            editor,
            range,
            onExit: (props as any).onExit,
            onSelectBlock: this.options.onSelectBlock,
          })
        },
        render: () => {
          let component: ReactRenderer<{
            onKeyDown: (args: { event: KeyboardEvent }) => boolean
          }>

          let popup: Instance<Props>
          let isOpen = false
          let isMouseInteracting = false
          const onExitBase = (onCloseCallback: () => void) => {
            if (!isOpen) return
            // Delay cleanup if mouse interaction is in progress
            if (isMouseInteracting) {
              setTimeout(() => {
                if (isOpen) {
                  onExitBase(onCloseCallback)
                }
              }, 100)
              return
            }
            isOpen = false
            popup?.destroy()
            component?.destroy()
            onCloseCallback()
            this.editor.view.dom.classList.remove('slash-command-open')
          }
          return {
            onStart: (props) => {
              if (!props.editor.isFocused) {
                return
              }

              isOpen = true
              this.options.onOpen()
              this.editor.view.dom.classList.add('slash-command-open')

              component = new ReactRenderer(CommandsListController, {
                editor: props.editor,
                props: {
                  ...props,
                  component: this.options.component,
                  onExit: () => {
                    onExitBase(this.options.onClose)
                  },
                  isOpen,
                  setMouseInteracting: (value: boolean) => {
                    isMouseInteracting = value
                  },
                },
              })

              const tabContent = document.querySelector<HTMLDivElement>(
                `#${tabContentId}`
              )
              if (!tabContent) {
                throw new Error('tabContent is null or undefined')
              }

              popup = tippy(tabContent, {
                getReferenceClientRect: () => {
                  const clientRect = props.clientRect?.()
                  if (!clientRect) {
                    throw new Error('clientRect is null or undefined')
                  }
                  return clientRect
                },
                appendTo: tabContent,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
              })
            },
            onUpdate: (props) => {
              component.updateProps({
                ...props,
                component: this.options.component,
                setMouseInteracting: (value: boolean) => {
                  isMouseInteracting = value
                },
              })

              popup?.setProps({
                getReferenceClientRect: () => {
                  const clientRect = props.clientRect?.()
                  if (!clientRect) {
                    throw new Error('clientRect is null or undefined')
                  }
                  return clientRect
                },
              })
              if (!props.editor.isFocused) {
                onExitBase(this.options.onClose)
                popup?.destroy()
                component?.destroy()
                return
              }
            },
            onKeyDown(props) {
              if (!isOpen) return false

              const handled = component?.ref?.onKeyDown?.(props)

              // If the component handled it, stop propagation
              if (handled) {
                return true
              }

              return false
            },
            onExit: () => {
              onExitBase(this.options.onClose)
            },
          }
        },
      }),
    ]
  },
})

type ControllerProps = {
  onExit: () => void
  items: CommandCategory[]
  command: (item: CommandCategory & { onExit: () => void }) => void
  component: React.FC<CommandListComponentProps>
  isOpen: boolean
  setMouseInteracting: (value: boolean) => void
}

export class CommandsListController extends React.Component<
  ControllerProps,
  { selectedIndex: number }
> {
  constructor() {
    // @ts-ignore
    super()
    this.state = {
      selectedIndex: 0,
    }
    this.selectItem = this.selectItem.bind(this)
  }

  onKeyDown({ event }: { event: KeyboardEvent }) {
    if (event.key === 'ArrowUp') {
      // dont stop propagation here plz
      return true
    }

    if (event.key === 'ArrowDown') {
      // dont stop propagation here plz
      return true
    }

    if (event.key === 'Enter') {
      // dont stop propagation here plz
      return true
    }

    if (event.key === 'Escape') {
      // dont stop propagation here plz
      return true
    }

    // ctrl+space
    if (event.key === 'Space' && event.ctrlKey) {
      // dont stop propagation here plz
      return true
    }

    return false
  }

  enterHandler() {
    this.selectItem(this.state.selectedIndex)
  }

  selectItem(index: number) {
    if (!this.props.isOpen) return
    const flatItems = this.props.items.flatMap((c) => c.commands)
    const item = flatItems[index]

    if (item) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.props.command({ ...(item as any), onExit: this.props.onExit })
    }
  }

  componentDidUpdate(prevProps: ControllerProps) {
    const prevFatItems = prevProps.items.flatMap((c) => c.commands)
    const flatItems = this.props.items.flatMap((c) => c.commands)
    if (prevFatItems.length !== flatItems.length) {
      this.setState({ selectedIndex: 0 })
    }
  }

  render() {
    const { items, component: Component } = this.props
    const { selectedIndex } = this.state

    const setSelectedIndex = (index: number) => {
      this.setState({ selectedIndex: index })
    }

    return (
      <Component
        items={items}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        selectItem={this.selectItem}
        onExit={this.props.onExit}
        setMouseInteracting={this.props.setMouseInteracting}
      />
    )
  }
}

// we create a store to perform "surgical" updates
export const useSlashCommandStore = create<{
  components: ComponentBlock[]
  allowedComponents: Map<RichTextBlock['id'], string[] | null>
  getAcceptedComponents: (richTextId: RichTextBlock['id']) => ComponentBlock[]
  setAcceptedComponents: (KV: {
    richTextId: RichTextBlock['id']
    acceptedComponents: string[] | null
  }) => void
  commands: CommandCategory[]
}>((set, get) => ({
  components: [],
  commands: [],
  allowedComponents: new Map(),
  setAcceptedComponents: ({ richTextId, acceptedComponents }) => {
    set((state) => ({
      ...state,
      allowedComponents: new Map(
        state.allowedComponents.set(richTextId, acceptedComponents)
      ),
    }))
  },
  getAcceptedComponents: (richTextId: RichTextBlock['id']) => {
    const state = get()
    const allowedComponents = state.allowedComponents.get(richTextId)
    const components = state.components
    if (!allowedComponents) return []
    return components.filter((component) =>
      allowedComponents.includes(idToSourceBlockId(component))
    )
  },
}))

/**
 * Reustable logig to filter the slash command.
 */
export const filterCommandCategory = (
  query: string,
  commandCategory: CommandCategory
): false | CommandCategory => {
  const nameMatches = queryMatches(query, commandCategory.name)

  if (nameMatches) {
    return commandCategory
  }

  const _filteredCommands = commandCategory.commands.filter((c) => {
    return matchesCommand(query, c)
  })

  if (_filteredCommands.length > 0) {
    return { ...commandCategory, commands: _filteredCommands }
  }

  return false
}

export const matchesCommand = (query: string, command: Command): boolean => {
  const titleMatch = queryMatches(query, command.title)
  if (titleMatch) return true

  const words = command.title.split(' ')

  const wordsMatch = words.some((w) => queryMatches(query, w))
  if (wordsMatch) return true

  const keywordMatch = command.keywords?.some((k) => {
    return queryMatches(query, k)
  })

  return !!keywordMatch
}
